import { useState, useEffect } from "react";
import { BrandLogo } from "@/components/BrandLogo";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, AlertTriangle, Eye, EyeOff, Mail, ShieldCheck } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";

export default function AcceptInvitePage() {
  useDocumentTitle("Accept invitation", "Set up your CRM account.");
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/invitations/lookup/${token}`);
        if (!cancelled) {
          setInvitation(data);
          setName(data.name || "");
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || "Invalid invitation link");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (password.length < 8) { setSubmitError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setSubmitError("Passwords don't match"); return; }
    if (!name.trim()) { setSubmitError("Please enter your full name"); return; }
    setSubmitting(true);
    try {
      await api.post("/invitations/accept", { token, name: name.trim(), password, phone });
      // Refresh auth context (cookie was just set)
      await checkAuth();
      navigate("/", { replace: true });
    } catch (err) {
      setSubmitError(err.response?.data?.detail || "Couldn't complete signup");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="accept-invite-page">
      <div className="w-full max-w-md bg-white rounded-lg border border-border p-8">
        <div className="flex items-center gap-2 mb-6">
          <BrandLogo size="md" />
        </div>

        {loading && <p className="text-sm text-muted-foreground">Verifying invitation...</p>}

        {!loading && error && (
          <div className="text-center py-4">
            <AlertTriangle size={36} className="mx-auto text-red-600 mb-3" />
            <h1 className="font-heading font-bold text-xl mb-2">Invitation Invalid</h1>
            <p className="text-sm text-muted-foreground mb-4" data-testid="invite-error-msg">{error}</p>
            <Button variant="outline" onClick={() => navigate("/login")}>Go to Login</Button>
          </div>
        )}

        {!loading && invitation && (
          <>
            <div className="mb-4">
              <h1 className="font-heading font-bold text-2xl mb-1">You've been invited</h1>
              <p className="text-sm text-muted-foreground mb-3">
                <strong>{invitation.invited_by_name}</strong> has invited you to join as <span className="font-semibold capitalize text-foreground">{invitation.role}</span>.
              </p>
              <div className="bg-slate-50 border border-border rounded p-3 text-xs text-muted-foreground flex items-center gap-2">
                <Mail size={13} />
                <span data-testid="invite-email">{invitation.email}</span>
              </div>
            </div>

            {submitError && (
              <div className="mb-3 p-2.5 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs" data-testid="invite-submit-error">{submitError}</div>
            )}

            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs">Full Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="invite-name-input" />
              </div>
              <div>
                <Label className="text-xs">Phone (optional)</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+44 7700 900000" data-testid="invite-phone-input" />
              </div>
              <div>
                <Label className="text-xs">Set Password (min 8 chars)</Label>
                <div className="relative">
                  <Input type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="invite-password-input" />
                  <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Confirm Password</Label>
                <Input type={showPass ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="invite-confirm-input" />
              </div>
              <Button type="submit" className="w-full" disabled={submitting} data-testid="invite-submit-btn">
                <ShieldCheck size={14} className="mr-1.5" />
                {submitting ? "Creating account..." : "Accept & Create Account"}
              </Button>
            </form>

            <p className="text-[11px] text-muted-foreground mt-4 flex items-center gap-1.5 justify-center">
              <CheckCircle2 size={11} className="text-emerald-600" />
              Internal staff portal — invitation expires {new Date(invitation.expires_at).toLocaleDateString()}.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
